<script lang="ts" setup>
import type { TeacherArticle } from '~/models/Content/TeacherArticle'
import type { Home1to4 } from '~/models/Content/Home1to4'
import type { ContentSectionStandalone } from '~/models/Content/ContentSectionStandalone'
import type { ContentFeatured } from '~/models/Content/ContentFeatured'
import type { BaseItem } from '~/models/Content/BaseItem'
import { useI18n } from 'vue-i18n'
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuery } from '@tanstack/vue-query'
import useArrayUtils from '~/utils/arrayUtils'
import useAuthStore from '~/stores/auth'
import { GradeType } from '~/models/GradeType'
import { Subtree } from '~/models/Content/Subtree'
import { ContentType } from '~/models/Content/ContentType'
import { ColorName } from '~/models/Content/Color'
import useHome from '~/composables/useHome'
import { useAppColor } from '~/composables/useAppColor'
import useContentApi from '~/api/contentApi'
import CopyrightButton from '~/components/utils/CopyrightButton.vue'
import HomeSubjects from '~/components/home/HomeSubjects.vue'
import Home1To4FeaturedCard from '~/components/home/home1to4/Home1To4FeaturedCard.vue'
import Greeting from '~/components/home/Greeting.vue'
import DefaultMessages from '~/components/home/DefaultMessages.vue'
import BannerSharedContent from '~/components/home/BannerSharedContent.vue'
import BannerArticles from '~/components/home/BannerArticles.vue'

useAppColor(ColorName.Green40)

const { t } = useI18n()
const { themeColor } = useAppColor()
const { isStudent } = storeToRefs(useAuthStore())
const { findContents } = useContentApi()
const { truthy } = useArrayUtils()

const { data } = useHome<Home1to4>(ContentType.Home1to4)

const hasTasks = ref(false)

const helpRelationId = computed(() => Number(data?.value?.teacherRelation?.destinationContentId ?? 0))
const yearlyRelationId = computed(() => Number(data?.value?.yearlyRelation?.destinationContentId ?? 0))
const featuredRelationId = computed(() => Number(data?.value?.featuredRelation?.destinationContentId ?? 0))
const sectionRelationIds = computed(() => data?.value?.sections?.destinationContentIds ?? [])

const relationIds = computed(() => [
  helpRelationId.value,
  yearlyRelationId.value,
  featuredRelationId.value,
  ...sectionRelationIds.value,
].filter(truthy<number>))

const { data: relations, isLoading } = useQuery({
  enabled: computed(() => relationIds.value.length > 0),
  queryKey: ['home-1-4-relations', 'dont-invalidate-on-grade-change'],
  queryFn: () => findContents<BaseItem>({
    subtreeCriterion: [Subtree.Content, Subtree.App],
    contentIdCriterion: relationIds.value,
    mainLocationCriterion: true,
  }, relationIds.value.length),
  select: data => [...data]
    .sort((a, b) => relationIds.value.indexOf(a.contentId) - relationIds.value.indexOf(b.contentId)),
  staleTime: Infinity,
})

const help = computed(() => (relations.value
  ?.find(({ contentId }) => helpRelationId.value === contentId) as TeacherArticle))
const featured = computed(() => (relations.value
  ?.find(({ contentId }) => featuredRelationId.value === contentId) as ContentFeatured))
const yearly = computed(() => (relations.value
  ?.find(({ contentId }) => yearlyRelationId.value === contentId) as BaseItem))
const sections = computed(() => (relations.value
  ?.filter(({ contentId }) => sectionRelationIds.value.includes(contentId)) as Array<ContentSectionStandalone>))
</script>

<template>
  <div class="flex h-full flex-col">
    <h1
      class="sr-only"
      v-text="t('home.title')"
    />
    <DefaultMessages class="relative z-10 mt-24 -mb-18 w-full" />
    <section :style="{ background: themeColor }" class="text-white">
      <div class="space-y-6">
        <div class="max-w-screen-au mx-auto px-4 pt-24 sm:px-8">
          <BannerSharedContent
            v-if="isStudent"
            v-model:has-tasks="hasTasks"
            class="mb-6"
          />
          <div class="flex gap-6">
            <h1
              class="sr-only"
              v-text="t('home.title')"
            />
          </div>
        </div>
        <div class="max-w-screen-au relative z-20 mx-auto grid size-full grid-cols-1 items-center px-4 text-clip transition-all sm:px-8 lg:grid-cols-2 lg:pb-40" :class="{ 'lg:pb-56': hasTasks }">
          <div>
            <Greeting :article="help" class="mb-4 min-h-[12rem] w-fit xl:float-left xl:mr-4 xl:mb-0" />
            <HomeSubjects class="contents" />
          </div>
          <div class="pointer-events-none relative right-0 bottom-0 size-full lg:absolute lg:w-1/2 2xl:-right-12">
            <div class="relative flex size-full items-center justify-center md:justify-start">
              <Home1To4FeaturedCard
                v-if="featured"
                :featured="featured"
                :item="yearly"
                :has-tasks
              />
              <div class="absolute right-8 bottom-0 lg:bottom-20">
                <CopyrightButton
                  :copyright="t('aschehoug')"
                  class="pointer-events-auto"
                  is-animation
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    <BannerArticles
      :grade-type="GradeType.Grade1To4"
      :is-loading="isLoading"
      :sections="sections ?? []"
      class="max-w-screen-au mx-auto px-4 pt-6 sm:px-8 lg:pt-0"
    />
  </div>
</template>
